import React, { useState, useRef } from "react";
import styled from "styled-components";
import COLORS from "common/Colors";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import { useTranslation } from "react-i18next";
import { Modal, Switch } from "antd";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const SIDEBAR_W = 260;
const SIDEBAR_COLLAPSED_W = 48;

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 1.5;
const ZOOM_STEP = 0.1;

// UI-only mock of a visual workflow builder (n8n-style canvas). Node
// positions are hand-laid-out for this one seeded workflow — no drag/zoom,
// no execution wiring. Swap in a real graph lib (react-flow) if/when this
// needs to be editable or backed by live runs.

const NODE_W = 224;
const NODE_H = 88;

const NODES = [
  { id: "trigger", x: 20, y: 264, type: "trigger", title: "Weekly Trigger", subtitle: "Every Monday · 9:00 AM", Icon: ScheduleOutlinedIcon },
  { id: "filter", x: 288, y: 264, type: "filter", title: "Filter Candidates", subtitle: "Chennai · 3+ yrs · React · E-commerce exp", Icon: FilterAltOutlinedIcon },
  { id: "email", x: 556, y: 264, type: "email", title: "Send Availability Email", subtitle: "Ask for Friday 3:00 PM slot", Icon: MailOutlineIcon },
  { id: "branch", x: 824, y: 264, type: "branch", title: "Available?", subtitle: "Based on candidate's reply", Icon: CallSplitOutlinedIcon },
  { id: "schedule", x: 1092, y: 128, type: "success", title: "Schedule Interview", subtitle: "Fri 3:00 PM · John, Sam (CC)", Icon: EventAvailableOutlinedIcon },
  { id: "calendar", x: 1360, y: 128, type: "success", title: "Create Calendar Invite", subtitle: "Auto-added to all attendees", Icon: EventOutlinedIcon },
  { id: "reschedule", x: 1092, y: 404, type: "warning", title: "Coordinate Reschedule", subtitle: "Propose new time from reply", Icon: UpdateOutlinedIcon },
  { id: "report", x: 1628, y: 264, type: "report", title: "Send Weekly Report", subtitle: "Fri 8:00 AM · Full summary to you", Icon: AssessmentOutlinedIcon },
];

const NODE_TYPE_COLOR = {
  trigger: "#7C5CFC",
  filter: COLORS.PRIMARY,
  email: "#0EA5A5",
  branch: "#F8B505",
  success: COLORS.SUCCESS,
  warning: "#F08A3C",
  report: COLORS.PRIMARY,
};

const center = (n) => ({ x: n.x + NODE_W, y: n.y + NODE_H / 2, left: n.x, leftY: n.y + NODE_H / 2 });

function edgePath(from, to) {
  const a = center(from);
  const b = { x: to.x, y: to.y + NODE_H / 2 };
  const dx = Math.max(60, (b.x - a.x) / 2);
  return `M ${a.x} ${a.y} C ${a.x + dx} ${a.y}, ${b.x - dx} ${b.y}, ${b.x} ${b.y}`;
}

const byId = Object.fromEntries(NODES.map((n) => [n.id, n]));

const EDGES = [
  { from: "trigger", to: "filter" },
  { from: "filter", to: "email" },
  { from: "email", to: "branch" },
  { from: "branch", to: "schedule", label: "Yes", dashed: false },
  { from: "branch", to: "reschedule", label: "No", dashed: false },
  { from: "schedule", to: "calendar" },
  { from: "calendar", to: "report" },
  { from: "reschedule", to: "report", dashed: true },
];

const CANVAS_W = 1628 + NODE_W + 40;
const CANVAS_H = 560;

function WorkflowAgents() {
  const { t } = useTranslation();
  const [active, setActive] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [handTool, setHandTool] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const scrollRef = useRef(null);
  const panRef = useRef(null);

  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setZoom(1);

  const startPan = (e) => {
    if (!handTool || !scrollRef.current) return;
    panRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: scrollRef.current.scrollLeft,
      scrollTop: scrollRef.current.scrollTop,
    };
    const onMove = (ev) => {
      if (!panRef.current || !scrollRef.current) return;
      scrollRef.current.scrollLeft = panRef.current.scrollLeft - (ev.clientX - panRef.current.startX);
      scrollRef.current.scrollTop = panRef.current.scrollTop - (ev.clientY - panRef.current.startY);
    };
    const onUp = () => {
      panRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <Wrap>
      <ListPanel $collapsed={sidebarCollapsed}>
        <ListHeader $collapsed={sidebarCollapsed}>
          {!sidebarCollapsed && <span>{t("Workflow Agents")}</span>}
          <CollapseButton
            type="button"
            title={sidebarCollapsed ? t("Expand") : t("Collapse")}
            onClick={() => setSidebarCollapsed((v) => !v)}
          >
            {sidebarCollapsed ? <ChevronRightIcon style={{ fontSize: 18 }} /> : <ChevronLeftIcon style={{ fontSize: 18 }} />}
          </CollapseButton>
        </ListHeader>

        {sidebarCollapsed ? (
          <CollapsedIconButton type="button" title={t("Weekly Candidate Screening & Scheduling")}>
            <SmartToyOutlinedIcon style={{ fontSize: 18 }} />
          </CollapsedIconButton>
        ) : (
          <>
            <WorkflowCard $selected>
              <WorkflowCardIcon>
                <SmartToyOutlinedIcon style={{ fontSize: 18 }} />
              </WorkflowCardIcon>
              <div style={{ flex: 1, minWidth: 0 }}>
                <WorkflowCardTitle>{t("Weekly Candidate Screening & Scheduling")}</WorkflowCardTitle>
                <WorkflowCardMeta>{t("Runs every Monday, 9:00 AM")}</WorkflowCardMeta>
              </div>
            </WorkflowCard>
            <EmptyHint>{t("More workflow agents you create will show up here.")}</EmptyHint>
          </>
        )}
      </ListPanel>

      <CanvasPanel>
        <CanvasToolbar>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <ToolbarTitle title="Weekly Candidate Screening & Scheduling">
              {t("Weekly Candidate Screening & Scheduling")}
            </ToolbarTitle>
            <StatusPill $active={active}>{active ? t("Active") : t("Inactive")}</StatusPill>
          </div>
          <ToolbarActions>
            <Switch size="small" checked={active} onChange={setActive} />
            <ButtonOutlined type="button" style={{ padding: "6px 14px", fontSize: 12.5 }}>
              <PlayArrowOutlinedIcon style={{ fontSize: 16, verticalAlign: -3, marginRight: 4 }} />
              {t("Run now")}
            </ButtonOutlined>
          </ToolbarActions>
        </CanvasToolbar>

        <CanvasScroll
          ref={scrollRef}
          onMouseDown={startPan}
          $handTool={handTool}
        >
          <CanvasSizer style={{ width: CANVAS_W * zoom, height: CANVAS_H * zoom }}>
          <Canvas
            style={{
              width: CANVAS_W,
              height: CANVAS_H,
              transform: `scale(${zoom})`,
              pointerEvents: handTool ? "none" : "auto",
            }}
          >
            <svg
              width={CANVAS_W}
              height={CANVAS_H}
              style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
            >
              <defs>
                <marker id="wf-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                  <path d="M0,0 L8,4 L0,8 Z" fill="#B7BFD6" />
                </marker>
              </defs>
              {EDGES.map((e) => {
                const from = byId[e.from];
                const to = byId[e.to];
                const a = center(from);
                const midX = (a.x + to.x) / 2;
                const midY = (a.y + to.y + NODE_H / 2) / 2;
                return (
                  <g key={`${e.from}-${e.to}`}>
                    <path
                      d={edgePath(from, to)}
                      fill="none"
                      stroke="#B7BFD6"
                      strokeWidth={1.6}
                      strokeDasharray={e.dashed ? "5 4" : undefined}
                      markerEnd="url(#wf-arrow)"
                    />
                    {e.label && (
                      <g>
                        <rect x={midX - 15} y={midY - 10} width={30} height={18} rx={9} fill="#FFFFFF" stroke="#E5E9F7" />
                        <text x={midX} y={midY + 3} textAnchor="middle" fontSize="10.5" fontWeight="600" fill={e.label === "Yes" ? COLORS.SUCCESS : COLORS.ERROR}>
                          {e.label}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>

            {NODES.map((n) => (
              <Node
                key={n.id}
                style={{ left: n.x, top: n.y, width: NODE_W, height: NODE_H }}
                $color={NODE_TYPE_COLOR[n.type]}
                onClick={() => setSelectedNode(n)}
              >
                <NodeIcon $color={NODE_TYPE_COLOR[n.type]}>
                  <n.Icon style={{ fontSize: 18 }} />
                </NodeIcon>
                <div style={{ minWidth: 0 }}>
                  <NodeTitle>{n.title}</NodeTitle>
                  <NodeSubtitle title={n.subtitle}>{n.subtitle}</NodeSubtitle>
                </div>
              </Node>
            ))}
          </Canvas>
          </CanvasSizer>
        </CanvasScroll>

        <ZoomControls>
          <ZoomButton type="button" $active={!handTool} onClick={() => setHandTool(false)} title={t("Select tool")}>
            <NearMeOutlinedIcon style={{ fontSize: 15 }} />
          </ZoomButton>
          <ZoomButton type="button" $active={handTool} onClick={() => setHandTool(true)} title={t("Hand tool (drag to pan)")}>
            <PanToolOutlinedIcon style={{ fontSize: 15 }} />
          </ZoomButton>
          <ZoomDivider />
          <ZoomButton type="button" onClick={zoomOut} disabled={zoom <= ZOOM_MIN} title={t("Zoom out")}>
            <RemoveIcon style={{ fontSize: 16 }} />
          </ZoomButton>
          <ZoomPct type="button" onClick={zoomReset} title={t("Reset zoom")}>
            {Math.round(zoom * 100)}%
          </ZoomPct>
          <ZoomButton type="button" onClick={zoomIn} disabled={zoom >= ZOOM_MAX} title={t("Zoom in")}>
            <AddIcon style={{ fontSize: 16 }} />
          </ZoomButton>
          <ZoomDivider />
          <ZoomButton type="button" onClick={zoomReset} title={t("Fit to 100%")}>
            <CropFreeOutlinedIcon style={{ fontSize: 15 }} />
          </ZoomButton>
        </ZoomControls>
      </CanvasPanel>


      <Modal
        open={!!selectedNode}
        onCancel={() => setSelectedNode(null)}
        footer={null}
        centered
        width={380}
        closeIcon={<CloseOutlinedIcon style={{ fontSize: 18 }} />}
      >
        {selectedNode && (
          <NodeDetail>
            <NodeIcon $color={NODE_TYPE_COLOR[selectedNode.type]} style={{ marginBottom: 10 }}>
              <selectedNode.Icon style={{ fontSize: 20 }} />
            </NodeIcon>
            <NodeDetailTitle>{selectedNode.title}</NodeDetailTitle>
            <NodeDetailSubtitle>{selectedNode.subtitle}</NodeDetailSubtitle>
            <NodeDetailFooter>
              <DescriptionOutlinedIcon style={{ fontSize: 14 }} />
              {t("Step configuration is read-only in this preview.")}
            </NodeDetailFooter>
          </NodeDetail>
        )}
      </Modal>
    </Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  gap: 16px;
  min-height: 560px;
`;

const ListPanel = styled.div`
  width: ${(p) => (p.$collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W)}px;
  flex-shrink: 0;
  border: 1px solid #e5e9f7;
  border-radius: 12px;
  padding: ${(p) => (p.$collapsed ? "14px 8px" : "14px")};
  background: #fafbfe;
  transition: width 0.15s ease, padding 0.15s ease;
  overflow: hidden;
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$collapsed ? "center" : "space-between")};
  font-size: 13.5px;
  font-weight: 700;
  color: ${COLORS.TEXT.PRIMARY};
  margin-bottom: 12px;
`;

const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: ${COLORS.TEXT.PRIMARY};
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: #eef1f8; color: ${COLORS.BLACK}; }
`;

const CollapsedIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 0 auto 10px;
  border: 1px solid #e5e9f7;
  background: #fff;
  border-radius: 8px;
  color: ${COLORS.PRIMARY};
  cursor: pointer;
  &:hover { border-color: ${COLORS.PRIMARY}; background: ${COLORS.SECONDARY}; }
`;

const WorkflowCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$selected ? COLORS.PRIMARY : "#E5E9F7")};
  background: ${(p) => (p.$selected ? COLORS.SECONDARY : "#FFFFFF")};
  cursor: pointer;
`;

const WorkflowCardIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.PRIMARY};
  color: #fff;
`;

const WorkflowCardTitle = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: ${COLORS.BLACK};
  line-height: 1.3;
`;

const WorkflowCardMeta = styled.div`
  font-size: 11px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-top: 2px;
`;

const EmptyHint = styled.div`
  margin-top: 14px;
  font-size: 11px;
  color: ${COLORS.GREY};
  line-height: 1.5;
`;

const CanvasPanel = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  border: 1px solid #e5e9f7;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CanvasToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e9f7;
  background: #fff;
`;

const ToolbarTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.BLACK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusPill = styled.span`
  flex-shrink: 0;
  font-size: 10.5px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  color: ${(p) => (p.$active ? COLORS.SUCCESS : COLORS.GREY)};
  background: ${(p) => (p.$active ? "#E8F8EF" : "#F1F2F5")};
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const CanvasScroll = styled.div`
  flex: 1;
  overflow: auto;
  background-color: #f6f7fb;
  background-image: radial-gradient(#d7dcec 1px, transparent 1px);
  background-size: 18px 18px;
  cursor: ${(p) => (p.$handTool ? "grab" : "default")};
  &:active { cursor: ${(p) => (p.$handTool ? "grabbing" : "default")}; }
`;

const CanvasSizer = styled.div`
  position: relative;
`;

const Canvas = styled.div`
  position: relative;
  transform-origin: top left;
`;

const ZoomControls = styled.div`
  position: absolute;
  right: 16px;
  bottom: 16px;
  display: flex;
  align-items: center;
  gap: 2px;
  background: #fff;
  border: 1px solid #e5e9f7;
  border-radius: 10px;
  padding: 4px;
  box-shadow: 0 4px 14px rgba(20, 30, 70, 0.1);
`;

const ZoomButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  background: ${(p) => (p.$active ? COLORS.SECONDARY : "transparent")};
  border-radius: 6px;
  color: ${(p) => (p.$active ? COLORS.PRIMARY : COLORS.TEXT.PRIMARY)};
  cursor: pointer;

  &:hover:not(:disabled) { background: ${(p) => (p.$active ? COLORS.SECONDARY : "#f1f2f5")}; color: ${(p) => (p.$active ? COLORS.PRIMARY : COLORS.BLACK)}; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const ZoomPct = styled.button`
  border: none;
  background: transparent;
  min-width: 42px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${COLORS.TEXT.PRIMARY};
  cursor: pointer;
  padding: 4px 2px;
  &:hover { color: ${COLORS.BLACK}; }
`;

const ZoomDivider = styled.div`
  width: 1px;
  height: 18px;
  background: #e5e9f7;
  margin: 0 2px;
`;

const Node = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  background: #fff;
  border: 1px solid #e5e9f7;
  border-left: 3px solid ${(p) => p.$color};
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(20, 30, 70, 0.06);
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(20, 30, 70, 0.12);
    transform: translateY(-1px);
  }
`;

const NodeIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.$color}1A;
  color: ${(p) => p.$color};
`;

const NodeTitle = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: ${COLORS.BLACK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NodeSubtitle = styled.div`
  font-size: 10.5px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NodeDetail = styled.div`
  padding-top: 4px;
`;

const NodeDetailTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.BLACK};
`;

const NodeDetailSubtitle = styled.div`
  font-size: 12.5px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-top: 4px;
  line-height: 1.5;
`;

const NodeDetailFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eef1f8;
  font-size: 11px;
  color: ${COLORS.GREY};
`;

export default WorkflowAgents;
